import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import academyLogo from '@/assets/munilex-academy-logo.png';
import { useAuth } from '@/hooks/useAuth';
import { resolvePostAuthRoute } from '@/lib/authRedirect';

// Use public path so the preload <link> in index.html matches and the LCP image is discoverable in initial HTML
const munilexLogo = '/munilex-logo.png';

export function AuthLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { session, profile, isLoading } = useAuth();
  const isOpositores = location.pathname.includes('/opositores');

  // Si el usuario vuelve aquí ya autenticado (p.ej. tras OAuth Google), llévalo a su área.
  // Excluir /auth/reset-password y /auth/verify-* para no romper esos flujos.
  useEffect(() => {
    if (isLoading || !session) return;
    const path = location.pathname;
    if (path.includes('reset-password') || path.includes('verify') || path.includes('pending')) return;

    const plan = profile?.plan || 'free';
    const intent = (typeof window !== 'undefined' && localStorage.getItem('munilex_auth_intent')) || null;
    if (intent) localStorage.removeItem('munilex_auth_intent');

    const target = resolvePostAuthRoute({
      plan,
      opositorBody: profile?.opositor_body,
      intent: intent === 'opositor' || intent === 'funcionario' ? intent : null,
    });

    navigate(target, { replace: true });
  }, [session, profile, isLoading, location.pathname, navigate]);

  // Funcionarios: gradiente Munilex (turquesa/menta)
  // Opositores: fondo OSCURO elegante con acento dorado para que el logo MunilexAcademy luzca
  const wrapperClass = isOpositores
    ? 'min-h-screen bg-gradient-to-br from-[hsl(0,0%,7%)] via-[hsl(0,0%,10%)] to-[hsl(38,30%,14%)] flex items-center justify-center p-4'
    : 'min-h-screen bg-gradient-to-br from-accent to-[hsl(var(--munilex-light))] dark:from-background dark:to-[hsl(210,60%,15%)] flex items-center justify-center p-4';

  const cardClass = isOpositores
    ? 'bg-card text-card-foreground rounded-2xl shadow-[0_25px_60px_-15px_rgba(0,0,0,0.6)] border-2 border-[hsl(45,80%,55%)]/40 p-6 sm:p-8'
    : 'bg-card text-card-foreground rounded-2xl shadow-xl border border-border p-6 sm:p-8';

  return (
    <div className={wrapperClass}>
      <div className="w-full max-w-md">
        {/* Logo MunilexAcademy fuera de la card, sobre el fondo oscuro, para máximo contraste */}
        {isOpositores && (
          <div className="flex justify-center mb-6">
            <img
              src={academyLogo}
              alt="MunilexAcademy"
              className="h-16 sm:h-20 object-contain"
              decoding="async"
            />
          </div>
        )}

        <div className={cardClass}>
          {!isOpositores && (
            <div className="flex justify-center mb-6 sm:mb-8">
              <picture>
                <source srcSet="/munilex-logo.webp" type="image/webp" />
                <img
                  src="/munilex-logo-optimized.png"
                  alt="Munilex"
                  className="h-10 sm:h-12"
                  width="169"
                  height="48"
                  decoding="async"
                />
              </picture>
            </div>
          )}
          {isOpositores && (
            <div className="text-center mb-6">
              <span className="inline-block px-3 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase bg-[hsl(45,80%,55%)]/15 text-[hsl(38,75%,38%)]">
                Acceso opositores
              </span>
            </div>
          )}
          <Outlet />
        </div>
      </div>
    </div>
  );
}
